// src/hooks/order/useRecogeEnBodega.ts
// Hook para elección de bodega + "Cliente recoge en bodega"

import { useState, useEffect, useCallback } from 'react'
import type { OrderData } from '../../types/orderFormTypes'
import {
  readBodegaRecogida,
  readOrderMetaValue,
  recogeFlagFromRespuesta,
  type BodegaRecogidaSeleccion,
  type RecogeRespuesta
} from '../../utils/orderBodegaRecogida'

export type { BodegaRecogidaSeleccion, RecogeRespuesta }

/** Bodegas con misma dirección/stock que Xerox */
export const BODEGA_IDS_TIPO_XEROX: readonly BodegaRecogidaSeleccion[] = ['95', '93', '94']

/** ML fullfillment (93) y ML Distrito Cafetero (94): recoge en bodega siempre No */
export const BODEGA_IDS_SIN_RECOGE: readonly BodegaRecogidaSeleccion[] = ['93', '94']

export function isBodegaTipoXerox(sel: string): boolean {
  return BODEGA_IDS_TIPO_XEROX.includes(sel as BodegaRecogidaSeleccion)
}

export function isBodegaSinRecoge(sel: string): boolean {
  return BODEGA_IDS_SIN_RECOGE.includes(sel as BodegaRecogidaSeleccion)
}

function lecturaInicial(initialData: OrderData | undefined): {
  idMarket: BodegaRecogidaSeleccion
  recogeRespuesta: RecogeRespuesta
} {
  const read = readBodegaRecogida(initialData)
  if (isBodegaSinRecoge(read.idMarket)) {
    return { idMarket: read.idMarket, recogeRespuesta: 'no' }
  }
  return read
}

function getFechaEntregaFromInitial(initialData: OrderData | undefined): string {
  if (!initialData) return ''
  const fromMeta = readOrderMetaValue(initialData.meta_data, 'fecha_entrega')
    || readOrderMetaValue(initialData, 'fecha_entrega')
  if (fromMeta) return fromMeta.slice(0, 10)
  const root = (initialData as Record<string, unknown>).fecha_entrega
  if (root != null && String(root).trim() !== '') return String(root).slice(0, 10)
  return ''
}

interface UseRecogeEnBodegaProps {
  initialData?: OrderData
  mode: 'view' | 'edit' | 'create'
  setFormData: React.Dispatch<React.SetStateAction<OrderData>>
}

interface UseRecogeEnBodegaReturn {
  /** Hay bodega seleccionada (equivalente al antiguo switch "Elige bodega") */
  eligeBodega: boolean
  recogeEnBodega: boolean
  recogeRespuesta: RecogeRespuesta
  handleRecogeRespuestaChange: (value: RecogeRespuesta) => void
  bodegaRecogidaSeleccion: BodegaRecogidaSeleccion
  handleBodegaRecogidaSeleccionChange: (value: BodegaRecogidaSeleccion) => void
  fechaEntregaActiva: boolean
  handleFechaEntregaActivaChange: (checked: boolean) => void
  fechaEntrega: string
  handleFechaEntregaChange: (value: string) => void
}

/** Xerox / ML fullfillment / ML Distrito Cafetero (id_market 95, 93, 94) */
const BODEGA_XEROX_ADDRESS = {
  address_1: 'Carrera 43A #15 sur 15, Medellin, Antioquia',
  address_2: '',
  state: '05',
  city: '05001'
}

/** Ecommerce, En tránsito (97) y HOMECENTER (98) comparten dirección */
const BODEGA_ECOMMERCE_ADDRESS = {
  address_1: 'Calle 25 A # 43B - 199, Medellin, Antioquia',
  address_2: '',
  state: '05',
  city: '05001'
}

function patchDireccionRecogida(sel: BodegaRecogidaSeleccion) {
  if (isBodegaTipoXerox(sel)) return BODEGA_XEROX_ADDRESS
  if (sel === 'ecommerce' || sel === '97' || sel === '98') return BODEGA_ECOMMERCE_ADDRESS
  return {
    address_1: '',
    address_2: '',
    state: '',
    city: ''
  }
}

function clearDireccionesYTransportadora(
  setFormData: React.Dispatch<React.SetStateAction<OrderData>>
) {
  setFormData((prev) => ({
    ...prev,
    billingAddress: {
      ...prev.billingAddress,
      address_1: '',
      address_2: '',
      state: '',
      city: ''
    },
    shippingAddress: {
      ...prev.shippingAddress,
      address_1: '',
      address_2: '',
      state: '',
      city: ''
    },
    extraDetails: {
      ...prev.extraDetails,
      transportadora: ''
    }
  }))
}

export function useRecogeEnBodega({
  initialData,
  mode,
  setFormData
}: UseRecogeEnBodegaProps): UseRecogeEnBodegaReturn {
  const [bodegaRecogidaSeleccion, setBodegaRecogidaSeleccion] = useState<BodegaRecogidaSeleccion>(() =>
    lecturaInicial(initialData).idMarket
  )

  const [recogeRespuesta, setRecogeRespuesta] = useState<RecogeRespuesta>(() =>
    lecturaInicial(initialData).recogeRespuesta
  )

  const initialFecha = getFechaEntregaFromInitial(initialData)
  const [fechaEntregaActiva, setFechaEntregaActiva] = useState<boolean>(() => !!initialFecha)
  const [fechaEntrega, setFechaEntrega] = useState<string>(() => initialFecha)

  const recogeEnBodega = recogeRespuesta === 'si'
  const eligeBodega = bodegaRecogidaSeleccion !== ''

  useEffect(() => {
    const lectura = lecturaInicial(initialData)
    if (lectura.idMarket) {
      setBodegaRecogidaSeleccion((prev) => prev || lectura.idMarket)
    }
    if (lectura.recogeRespuesta) {
      setRecogeRespuesta((prev) => prev || lectura.recogeRespuesta)
    }
    if (lectura.idMarket || lectura.recogeRespuesta) {
      setFormData((prev) => {
        const already = readBodegaRecogida(prev)
        const nextMarket = already.idMarket || lectura.idMarket
        const nextRecoge = already.recogeRespuesta || lectura.recogeRespuesta
        const nextFlag = recogeFlagFromRespuesta(nextRecoge)
        const rootMarket = (prev as { id_market?: string }).id_market
        const sameMarket = !nextMarket || (prev.extraDetails?.id_market === nextMarket && rootMarket === nextMarket)
        const sameFlag = nextFlag == null || Number(prev.extraDetails?.recoge_en_bodega) === nextFlag
        if (sameMarket && sameFlag) return prev
        return {
          ...prev,
          ...(nextMarket ? { id_market: nextMarket } : {}),
          ...(nextFlag == null ? {} : { recoge_en_bodega: nextFlag }),
          extraDetails: {
            ...prev.extraDetails,
            ...(nextMarket ? { id_market: nextMarket } : {}),
            ...(nextFlag == null ? {} : { recoge_en_bodega: nextFlag })
          }
        }
      })
    }
    if ((mode === 'edit' || mode === 'view') && initialData) {
      const fecha = getFechaEntregaFromInitial(initialData)
      setFechaEntrega((prev) => prev || fecha)
      if (fecha) setFechaEntregaActiva((prev) => prev || true)
    }
  }, [mode, initialData, setFormData])

  // Direcciones solo cuando hay bodega + Cliente recoge = Sí
  useEffect(() => {
    if (!eligeBodega || !recogeEnBodega || !bodegaRecogidaSeleccion) return
    const patch = patchDireccionRecogida(bodegaRecogidaSeleccion)
    if (!patch) return
    setFormData((prev) => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        address_1: patch.address_1,
        address_2: patch.address_2,
        state: patch.state,
        city: patch.city
      },
      shippingAddress: {
        ...prev.shippingAddress,
        address_1: patch.address_1,
        address_2: patch.address_2,
        state: patch.state,
        city: patch.city
      }
    }))
  }, [eligeBodega, recogeEnBodega, bodegaRecogidaSeleccion, setFormData])

  const handleBodegaRecogidaSeleccionChange = useCallback((value: BodegaRecogidaSeleccion) => {
    if (!value) return
    setBodegaRecogidaSeleccion(value)
    const recoge: RecogeRespuesta = isBodegaSinRecoge(value) ? 'no' : ''
    if (recoge === 'no') {
      setRecogeRespuesta('no')
      clearDireccionesYTransportadora(setFormData)
    }
    const flag = recogeFlagFromRespuesta(recoge)
    setFormData((prev) => ({
      ...prev,
      id_market: value,
      ...(flag == null ? {} : { recoge_en_bodega: flag }),
      extraDetails: {
        ...prev.extraDetails,
        id_market: value,
        ...(flag == null ? {} : { recoge_en_bodega: flag })
      }
    }))
  }, [setFormData])

  const handleRecogeRespuestaChange = useCallback((value: RecogeRespuesta) => {
    if (!value) return
    if (isBodegaSinRecoge(bodegaRecogidaSeleccion)) {
      setRecogeRespuesta('no')
      clearDireccionesYTransportadora(setFormData)
      setFormData((prev) => ({
        ...prev,
        recoge_en_bodega: 0,
        ...(bodegaRecogidaSeleccion ? { id_market: bodegaRecogidaSeleccion } : {}),
        extraDetails: {
          ...prev.extraDetails,
          recoge_en_bodega: 0,
          ...(bodegaRecogidaSeleccion ? { id_market: bodegaRecogidaSeleccion } : {})
        }
      }))
      return
    }
    setRecogeRespuesta(value)
    if (value === 'si') {
      setFormData((prev) => ({
        ...prev,
        recoge_en_bodega: 1,
        ...(bodegaRecogidaSeleccion ? { id_market: bodegaRecogidaSeleccion } : {}),
        extraDetails: {
          ...prev.extraDetails,
          transportadora: 'entrega_pdv',
          recoge_en_bodega: 1,
          ...(bodegaRecogidaSeleccion ? { id_market: bodegaRecogidaSeleccion } : {})
        }
      }))
    } else if (value === 'no') {
      clearDireccionesYTransportadora(setFormData)
      setFormData((prev) => ({
        ...prev,
        recoge_en_bodega: 0,
        ...(bodegaRecogidaSeleccion ? { id_market: bodegaRecogidaSeleccion } : {}),
        extraDetails: {
          ...prev.extraDetails,
          recoge_en_bodega: 0,
          ...(bodegaRecogidaSeleccion ? { id_market: bodegaRecogidaSeleccion } : {})
        }
      }))
    }
  }, [bodegaRecogidaSeleccion, setFormData])

  const handleFechaEntregaActivaChange = useCallback((checked: boolean) => {
    setFechaEntregaActiva(checked)
    if (!checked) {
      setFechaEntrega('')
    }
  }, [])

  const handleFechaEntregaChange = useCallback((value: string) => {
    setFechaEntrega(value)
  }, [])

  return {
    eligeBodega,
    recogeEnBodega,
    recogeRespuesta,
    handleRecogeRespuestaChange,
    bodegaRecogidaSeleccion,
    handleBodegaRecogidaSeleccionChange,
    fechaEntregaActiva,
    handleFechaEntregaActivaChange,
    fechaEntrega,
    handleFechaEntregaChange
  }
}
